import { motion } from 'framer-motion';
import { ArrowUpRight, BarChart3, Home, Users } from 'lucide-react';

const pillars = [
  {
    icon: BarChart3,
    title: 'Kontrol penuh dalam',
    accentTitle: 'genggaman',
    description:
      'Cukup login untuk memantau laporan occupancy, laporan keuangan, dan komplain dengan data yang diperbarui setiap hari.',
    accent: 'from-isk-teal/16 via-isk-teal/8 to-white',
    tone: 'bg-isk-teal text-white'
  },
  {
    icon: Users,
    title: 'Tenant nyaman,',
    accentTitle: 'okupansi terjaga',
    description:
      'Tenant mendapatkan pengalaman tinggal yang berstandar tinggi, dengan tim yang responsif dalam melayani keluhan setiap hari.',
    accent: 'from-isk-gold/20 via-isk-gold/8 to-white',
    tone: 'bg-isk-gold text-isk-text'
  },
  {
    icon: Home,
    title: 'Properti',
    accentTitle: 'selalu terawat',
    description:
      'Properti dirawat oleh tim yang berpengalaman untuk memastikan kenyamanan tenant dan menjaga properti untuk jangka lebih panjang.',
    accent: 'from-isk-dark/15 via-isk-dark/8 to-white',
    tone: 'bg-isk-dark text-white'
  }
];

const Features = () => {
  return (
    <section
      id="services"
      className="relative overflow-hidden bg-isk-bg px-6 py-20 md:px-10 md:py-28 lg:px-12"
      data-testid="features-section"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-16 top-10 h-48 w-48 rounded-full bg-isk-teal/8 blur-3xl" />
        <div className="absolute right-0 top-1/3 h-64 w-64 rounded-full bg-isk-gold/10 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-56 w-56 rounded-full bg-isk-dark/8 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto mb-14 max-w-3xl text-center"
        >
          <h2 className="mb-4 text-4xl font-bold tracking-tight text-isk-text md:text-5xl lg:text-6xl">
            <span className="bg-gradient-to-r from-[#C9A77E] via-[#F0D5A0] to-[#C9A77E] bg-clip-text text-transparent">Tenang.</span> <span className="text-isk-teal">Kami yang Urus.</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-isk-dark via-isk-teal to-isk-dark p-[1px] lg:col-span-5"
          >
            <div className="relative h-full overflow-hidden rounded-[calc(2rem-1px)] bg-gradient-to-br from-isk-dark via-isk-teal to-isk-dark p-8 text-white md:p-10">
              <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full border border-white/15" />
              <div className="absolute bottom-0 right-0 h-56 w-56 translate-x-1/4 translate-y-1/4 rounded-full bg-white/10 blur-3xl" />

              <div className="relative z-10 flex h-full flex-col">
                <h3 className="max-w-md text-3xl font-bold leading-tight md:text-4xl">
                  Tiga Pilar Layanan <span className="text-isk-gold">ISK.</span>
                </h3>

                <p className="mt-5 max-w-xl text-base leading-relaxed text-white/80 md:text-lg">
                  ISK hadir sebagai partner pengelolaan kost yang membantu operasional berjalan rapi, aset tetap dijaga kualitasnya, dan tenant mendapatkan pengalaman tinggal yang lebih nyaman, sehingga owner bisa lebih <span className="font-semibold text-isk-gold">tenang</span>.
                </p>

                <div className="mt-8 grid gap-3">
                  {[
                    'Pantau occupancy, keuangan, dan komplain dalam satu login',
                    'Tenant mendapatkan layanan yang responsif dan berstandar tinggi',
                    'Properti dirawat oleh tim yang berpengalaman untuk memastikan kenyamanan tenant dan menjaga properti untuk jangka lebih panjang'
                  ].map((point) => (
                    <div
                      key={point}
                      className="flex items-start gap-3 rounded-2xl border border-white/12 bg-white/10 px-4 py-3 backdrop-blur-sm"
                    >
                      <div className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-white/15">
                        <ArrowUpRight className="h-3.5 w-3.5 text-white" />
                      </div>
                      <p className="text-sm leading-relaxed text-white/90">{point}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 gap-6 lg:col-span-7">
            {pillars.map((pillar, index) => {
              const Icon = pillar.icon;

              return (
                <motion.div
                  key={`${pillar.title} ${pillar.accentTitle}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  className={`glass-card relative overflow-hidden rounded-[2rem] border border-white/50 bg-gradient-to-br ${pillar.accent} p-8`}
                  data-testid={`feature-${index}`}
                >
                  <div className="absolute right-6 top-6 text-6xl font-extrabold tracking-tight text-isk-teal/20">
                    0{index + 1}
                  </div>

                  <div className="relative z-10 flex h-full flex-col">
                    <div className="mb-6">
                      <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${pillar.tone}`}>
                        <Icon className="h-7 w-7" />
                      </div>
                    </div>

                    <h3 className="max-w-xl text-2xl font-bold leading-snug text-isk-text md:text-[1.75rem]">
                      {pillar.title} <span className="text-isk-teal">{pillar.accentTitle}</span>
                    </h3>

                    <p className="mt-4 text-base leading-relaxed text-isk-muted">
                      {pillar.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
